import { useState, useEffect } from 'react';

interface TrackerStats {
  totalInterested: number;
  zipCodes: string[];
  facilities: number;
  patients: number;
  lastUpdated: string;
}

const STATS_VERSION = 'v3';

const DEFAULT_STATS: TrackerStats = {
  totalInterested: 6,
  zipCodes: ['85351', '85373', '85374', '85375', '85338', '85254', '85301', '85302', '85303', '85345', '85381', '85382'],
  facilities: 3,
  patients: 3,
  lastUpdated: new Date().toISOString(),
};

function loadStats(): TrackerStats {
  try {
    const version = localStorage.getItem('gv_tracker_version');
    if (version !== STATS_VERSION) {
      // Clear stale data and reset to new defaults
      localStorage.setItem('gv_tracker_stats', JSON.stringify(DEFAULT_STATS));
      localStorage.setItem('gv_tracker_version', STATS_VERSION);
      return DEFAULT_STATS;
    }
    const stored = localStorage.getItem('gv_tracker_stats');
    if (stored) return JSON.parse(stored);
  } catch (_e) {
    // localStorage unavailable or data corrupted — fall through to defaults
  }
  localStorage.setItem('gv_tracker_stats', JSON.stringify(DEFAULT_STATS));
  localStorage.setItem('gv_tracker_version', STATS_VERSION);
  return DEFAULT_STATS;
}

export function recordFacilitySubmission(city: string) {
  const stats = loadStats();
  const zip = cityToZip(city);
  const updatedZips = zip && !stats.zipCodes.includes(zip)
    ? [...stats.zipCodes, zip]
    : stats.zipCodes;
  const updated: TrackerStats = {
    ...stats,
    totalInterested: stats.totalInterested + 1,
    facilities: stats.facilities + 1,
    zipCodes: updatedZips,
    lastUpdated: new Date().toISOString(),
  };
  localStorage.setItem('gv_tracker_stats', JSON.stringify(updated));
  window.dispatchEvent(new Event('gv_tracker_update'));
}

export function recordPatientSubmission(city: string) {
  const stats = loadStats();
  const zip = cityToZip(city);
  const updatedZips = zip && !stats.zipCodes.includes(zip)
    ? [...stats.zipCodes, zip]
    : stats.zipCodes;
  const updated: TrackerStats = {
    ...stats,
    totalInterested: stats.totalInterested + 1,
    patients: stats.patients + 1,
    zipCodes: updatedZips,
    lastUpdated: new Date().toISOString(),
  };
  localStorage.setItem('gv_tracker_stats', JSON.stringify(updated));
  window.dispatchEvent(new Event('gv_tracker_update'));
}

function cityToZip(city: string): string | null {
  const map: Record<string, string> = {
    'sun city': '85351',
    'sun city west': '85375',
    'peoria': '85381',
    'surprise': '85374',
    'goodyear': '85338',
    'scottsdale': '85254',
    'glendale': '85301',
    'phoenix': '85001',
    'avondale': '85323',
    'buckeye': '85326',
    'litchfield park': '85340',
    'tolleson': '85353',
    'youngtown': '85363',
  };
  return map[city.toLowerCase().trim()] || null;
}

function useCountUp(target: number, duration = 1200) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const step = Math.ceil(target / (duration / 16));
    const timer = setInterval(() => {
      start += step;
      if (start >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(start);
      }
    }, 16);
    return () => clearInterval(timer);
  }, [target, duration]);
  return count;
}

export default function InterestTracker() {
  const [stats, setStats] = useState<TrackerStats>(loadStats);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const onUpdate = () => setStats(loadStats());
    window.addEventListener('gv_tracker_update', onUpdate);
    return () => window.removeEventListener('gv_tracker_update', onUpdate);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.2 }
    );
    const el = document.getElementById('interest-tracker');
    if (el) observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const animatedTotal = useCountUp(visible ? stats.totalInterested : 0);
  const animatedZips = useCountUp(visible ? stats.zipCodes.length : 0);
  const animatedFacilities = useCountUp(visible ? stats.facilities : 0);
  const animatedPatients = useCountUp(visible ? stats.patients : 0);

  const metrics = [
    {
      icon: 'ri-user-heart-line',
      value: animatedTotal,
      label: 'Total Interested',
      sublabel: 'People & facilities tracking our launch',
      color: 'from-yellow-500/20 to-yellow-600/10',
      border: 'border-yellow-500/40',
      iconBg: 'bg-yellow-500/20',
      iconColor: 'text-yellow-500',
      valueColor: 'text-yellow-400',
    },
    {
      icon: 'ri-map-pin-2-line',
      value: animatedZips,
      label: 'Zip Codes Represented',
      sublabel: 'Across the West Valley region',
      color: 'from-yellow-600/15 to-black',
      border: 'border-yellow-500/30',
      iconBg: 'bg-yellow-500/15',
      iconColor: 'text-yellow-400',
      valueColor: 'text-yellow-300',
    },
    {
      icon: 'ri-hospital-line',
      value: animatedFacilities,
      label: 'Facilities Interested',
      sublabel: 'Assisted living, dialysis & rehab centers',
      color: 'from-yellow-500/20 to-yellow-600/10',
      border: 'border-yellow-500/40',
      iconBg: 'bg-yellow-500/20',
      iconColor: 'text-yellow-500',
      valueColor: 'text-yellow-400',
    },
    {
      icon: 'ri-wheelchair-line',
      value: animatedPatients,
      label: 'Private Patients',
      sublabel: 'Individuals seeking transportation',
      color: 'from-yellow-600/15 to-black',
      border: 'border-yellow-500/30',
      iconBg: 'bg-yellow-500/15',
      iconColor: 'text-yellow-400',
      valueColor: 'text-yellow-300',
    },
  ];

  return (
    <section id="interest-tracker" className="py-12 sm:py-20 bg-gradient-to-b from-slate-900 to-slate-800">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-14">
          <div className="inline-flex items-center gap-2 bg-yellow-500/10 border border-yellow-500/30 rounded-full px-4 py-2 mb-4 sm:mb-6">
            <span className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></span>
            <span className="text-yellow-500 font-bold text-xs sm:text-sm whitespace-nowrap">Live Interest Tracker</span>
          </div>
          <h2 className="text-2xl sm:text-4xl font-bold text-white mb-3 sm:mb-4">
            West Valley Is Ready
          </h2>
          <p className="text-gray-400 text-sm sm:text-lg max-w-2xl mx-auto px-2">
            Real-time snapshot of community interest as we prepare to launch Guardian Veterans transportation services.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-8 sm:mb-12">
          {metrics.map((m, i) => (
            <div
              key={i}
              className={`bg-gradient-to-br ${m.color} border ${m.border} rounded-2xl p-4 sm:p-7 flex flex-col items-center text-center transition-all hover:scale-105 hover:border-yellow-500 duration-300`}
            >
              <div className={`w-10 h-10 sm:w-14 sm:h-14 ${m.iconBg} rounded-xl flex items-center justify-center mb-3 sm:mb-4`}>
                <i className={`${m.icon} ${m.iconColor} text-lg sm:text-2xl`}></i>
              </div>
              <div className={`text-3xl sm:text-5xl font-extrabold ${m.valueColor} mb-1 sm:mb-2 tabular-nums`}>
                {m.value}
                <span className="text-yellow-500/60 text-xl sm:text-3xl">+</span>
              </div>
              <div className="text-white font-bold text-xs sm:text-base mb-1 leading-tight">{m.label}</div>
              <div className="text-gray-500 text-xs leading-relaxed hidden sm:block">{m.sublabel}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
